import { addDays } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { togglePinFilterField } from '~/components/AlertsPage/AlertsPage.data';
import { updateFilterValues } from '~/components/AnalyzerPage/AnalyzerPage.utils';
import { updateFilterPinnedProperty } from '~/components/Modals/AddFilterModal/AddFilterModal.utils';
import { getOptions } from '~/components/Modals/AlertModal/AlertModal.options';
import {
  processData,
  validateFiltersForm,
} from '~/components/Modals/AlertModal/AlertModal.utils';
import { flattenFolderTree } from '~/components/SearchV2/SearchV2.utils';
import EcDatepicker from '~/components/Shared/EcDatepicker/EcDatepicker';
import EcFiltersSection from '~/components/Shared/EcFiltersSection/EcFiltersSection';
import EcModal from '~/components/Shared/EcModal/EcModal';
import EcMultipleSelectValue from '~/components/Shared/EcMultipleSelectValue/EcMultipleSelectValue';
import { Button } from '~/eds/src/components/Button/Button';
import { Checkbox } from '~/eds/src/components/Checkbox/Checkbox';
import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { NumberInput } from '~/eds/src/components/NumberInput/NumberInput';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Section } from '~/eds/src/components/Section/Section';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { Nullable } from '~/eds/src/types/Nullable';
import type { UserAction } from '~/eds/src/types/UserAction';
import { parseDate } from '~/eds/src/utils/parseDate';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useClientId } from '~/hooks/useClientId';
import { useUsers } from '~/hooks/useUsers';
import { api } from '~/redux/api/createApi';
import { PilotField, PilotFieldSection } from '~/redux/api/methods/alert';
import { slices } from '~/redux/slices';
import { FlattenFolderTree } from '~/types/Automation';
import { FolderTree } from '~/types/FolderTree';
import { MODAL_DEPARTMENT_TREE, MODAL_FOLDER_TREE } from '~/types/modal.types';
import { PilotId } from '~/types/PilotId';
import { User } from '~/types/User';
import { getUserName } from '~/utils/user';

export const AddAlertPanel = ({
  hideModal,
  isDuplicated,
  alertId,
  step: initialStep,
  onItemCreate,
  onItemUpdate,
  width,
}: {
  width?: string;
  alertId?: string;
  step?: number;
  hideModal: () => void;
  isDuplicated?: boolean;
  onItemCreate: (data: any) => void;
  onItemUpdate?: (id: string, data: any) => void;
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const clientId = useClientId();
  const allUsers = useUsers();

  const enableLocalization = useFlag(FlagType.LanguageSelect);
  const { OCCURRENCE_PERIOD_OPTIONS, OCCURRENCE_END_TIMESPAN_OPTIONS } =
    getOptions(intl);

  const config = useSelector(slices.alerts.selectors.selectConfig);

  const [step, setStep] = useState<number>(initialStep ?? 1);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const editMode = !!alertId && !isDuplicated;

  const {
    name,
    message,
    occurrenceInterval,
    occurrencePeriod,
    startDate,
    occurrenceEndDate,
    occurrenceEndTimespan,
    occurrenceEndLimit,
    filters,
    filterOptions,
    provisionOptions,
    users,
    userFields,
    departments,
    excludedUsers,
    editAccess,
    userFieldsSuggestions,
    folders,
  } = config;

  const {
    data: folderTreeData,
    isLoading: isLoadingFolderTree,
    error: folderTreeError,
  } = api.endpoints.getFolderTree.useQuery(undefined);

  const {
    data: alertModalData,
    isLoading: isLoadingAlertModalData,
    error: alertModalDataError,
  } = api.endpoints.getAlert.useQuery(
    { userClient: clientId, alertId: alertId ?? undefined },
    { skip: !clientId },
  );

  const usersOptions = (users: Record<PilotId, User>) => {
    return Object.values(users).map((user) => ({
      label: getUserName(user, intl),
      value: user.id,
    }));
  };

  const setUserDataFormat = (users: PilotField[]) => {
    return users?.map((optionField) => ({
      label: optionField.label,
      value: optionField.id,
    }));
  };

  useEffect(() => {
    if (folderTreeData && alertModalData) {
      const folderTreeArray = flattenFolderTree(folderTreeData?.children ?? []);

      const folderTree = folderTreeArray.reduce(
        (acc: Record<string | number, FlattenFolderTree>, folder) => {
          acc[folder.value] = folder;
          return acc;
        },
        {} as Record<string | number, FlattenFolderTree>,
      );

      const {
        customFieldsRes,
        sections,
        provisionOptions: provisions,
        alertData,
      } = alertModalData;

      const newSections = sections.sections.map(
        (section: PilotFieldSection) => {
          return section.title === 'Other Fields' ? { ...section } : section;
        },
      );

      const filterQuery = alertData?.query ?? filters;

      const flattenedSections = (newSections as PilotFieldSection[]).reduce(
        (acc: PilotField[], val: PilotFieldSection) => {
          return acc.concat(val?.fields ?? []);
        },
        [] as PilotField[],
      );

      const mappedFiltersWithFieldValues = updateFilterValues(
        flattenedSections,
        filterQuery,
      );

      const parsedFolderList =
        alertData?.folders?.map((folder: FolderTree) => ({
          id: folder.id,
          name: folderTree[folder.id]?.display_value ?? 'Restricted',
        })) ??
        folders?.map((folder) => ({
          id: folder.id,
          name: folder?.name,
        }));

      dispatch(
        slices.alerts.actions.setConfig({
          ...config,
          name: alertData?.name ?? name,
          message: alertData?.message ?? message,
          occurrenceInterval:
            alertData?.occurrence_interval ?? occurrenceInterval,
          occurrencePeriod: alertData?.occurrence_period
            ? OCCURRENCE_PERIOD_OPTIONS.find(
                (option) => option.value === alertData?.occurrence_period,
              )
            : OCCURRENCE_PERIOD_OPTIONS.find((obj) => obj.value === 'Hour'),
          occurrenceEndTimespan: alertData?.occurrence_end_timespan
            ? OCCURRENCE_END_TIMESPAN_OPTIONS.find(
                (option) => option.value === alertData?.occurrence_end_timespan,
              )
            : OCCURRENCE_END_TIMESPAN_OPTIONS.find(
                (obj) => obj.value === 'never',
              ),
          startDate: alertData?.start_date ?? startDate,
          occurrenceEndDate: alertData?.end_date ?? occurrenceEndDate,
          occurrenceEndLimit: alertData?.recurrences ?? occurrenceEndLimit,
          folders: parsedFolderList,
          filters: mappedFiltersWithFieldValues,
          users: alertData?.users?.map((user: User) => user.id) ?? users,
          userFields:
            alertData?.user_fields?.map((field: PilotField) => field.id) ??
            userFields,
          excludedUsers:
            alertData?.excluded_users?.map((user: User) => user.id) ??
            excludedUsers,
          departments: alertData?.departments ?? departments,
          editAccess: alertData?.edit_access ?? editAccess,
          folderTree,
          userFieldsSuggestions: setUserDataFormat(
            customFieldsRes as unknown as PilotField[],
          ),
          filterOptions: newSections,
          provisionOptions: provisions.suggestions,
        }),
      );
    }
  }, [folderTreeData, alertModalData, alertId]);

  useEffect(() => {
    if (occurrenceEndTimespan?.value === 'never') {
      dispatch(
        slices.alerts.actions.setConfig({
          ...config,
          occurrenceEndDate: null,
          occurrenceEndLimit: null,
        }),
      );
    }
  }, [occurrenceEndTimespan]);

  const handleHide = () => {
    dispatch(slices.alerts.actions.resetAlert());

    setStep(1);
    setActiveModal(null);

    hideModal();
  };

  const updateFilterOptionUI = (filterId: string) => {
    const newFilterOptions = updateFilterPinnedProperty(
      filterId,
      filterOptions,
    );

    dispatch(
      slices.alerts.actions.setConfig({
        ...config,
        filterOptions: newFilterOptions,
      }),
    );
  };

  const handleOptionsChangeHandler = (filterId: string) => {
    updateFilterOptionUI(filterId);
    togglePinFilterField(filterId).catch(() => {
      toast({
        message: intl.formatMessage({
          id: 'AlertModal.pinFilterError',
          defaultMessage: "There's been an error with pinning this filter.",
          description: 'Error message when pinning a filter fails',
        }),
        status: 'danger',
      });
      updateFilterOptionUI(filterId);
    });
  };

  const handleCreateAlert = () => {
    const processedData = processData(config, true);
    onItemCreate(processedData);
    handleHide();
  };

  const handleUpdateAlert = () => {
    // Step 1: If creating, move to step 2
    if (step === 1 && !editMode) {
      setStep(2);
      return;
    }

    // Step 2: Validate filters first
    if (step === 2) {
      const { filtersFormValid, validatedFilters } =
        validateFiltersForm(filters);

      dispatch(
        slices.alerts.actions.setConfig({
          ...config,
          filters: validatedFilters,
        }),
      );

      if (!filtersFormValid) {
        toast({
          message: intl.formatMessage({
            id: 'AlertModal.filtersValidationError',
            defaultMessage:
              'Please make sure that none of the selected filters are blank or not set.',
            description: 'Filters validation error message',
          }),
          status: 'danger',
        });
        return;
      }

      // If creating, move to next step
      if (!editMode) {
        setStep(3);
        return;
      }
    }

    // Update existing alert (edit mode)
    if (editMode && alertId) {
      const processedData = processData(config, true);
      onItemUpdate?.(alertId, processedData);
      handleHide();
    }
  };

  const { title, actions } = useMemo(() => {
    let title = '';
    let actions: UserAction[] = [];

    switch (step) {
      case 1:
        title = editMode
          ? intl.formatMessage({
              id: 'AlertModal.editAlertBasicDetails',
              defaultMessage: 'Edit Alert → Basic Details',
              description: 'Edit alert modal title',
            })
          : intl.formatMessage({
              id: 'AlertModal.addAlertBasicDetails',
              defaultMessage: 'Add Alert → Basic Details (Step 1/3)',
              description: 'Add alert modal title step 1',
            });
        actions = [
          {
            text: intl.formatMessage({
              id: 'AlertModal.cancelFirstStep',
              defaultMessage: 'Cancel',
              description: 'Cancel button text for first step in Alert Modal',
            }),
            onClick: handleHide,
            level: 'tertiary' as const,
          },
          {
            text: editMode
              ? intl.formatMessage({
                  id: 'AlertModal.saveFirstStep',
                  defaultMessage: 'Save Alert',
                  description:
                    'Save alert button text for first step in Alert Modal',
                })
              : intl.formatMessage({
                  id: 'AlertModal.nextStepFiltersButton',
                  defaultMessage: 'Next Step: Filters',
                  description: 'Next step filters button text',
                }),
            level: 'primary' as const,
            onClick: handleUpdateAlert,
            disabled: editMode
              ? !name || !message
              : !(name && name.trim()) ||
                !(message && message.trim()) ||
                !occurrenceInterval,
          },
        ];
        break;
      case 2:
        title = editMode
          ? intl.formatMessage({
              id: 'AlertModal.editAlertFilters',
              defaultMessage: 'Edit Alert → Filters',
              description: 'Edit alert filters title',
            })
          : intl.formatMessage({
              id: 'AlertModal.addAlertFilters',
              defaultMessage: 'Add Alert → Filters (Step 2/3)',
              description: 'Add alert filters title',
            });
        actions = editMode
          ? [
              {
                text: intl.formatMessage({
                  id: 'AlertModal.cancelSecondStep',
                  defaultMessage: 'Cancel',
                  description:
                    'Cancel button text for second step in Alert Modal',
                }),
                onClick: handleHide,
                level: 'tertiary' as const,
              },
              {
                text: intl.formatMessage({
                  id: 'AlertModal.saveSecondStep',
                  defaultMessage: 'Save Alert',
                  description:
                    'Save alert button text for second step in Alert Modal',
                }),
                level: 'primary' as const,
                onClick: handleUpdateAlert,
              },
            ]
          : [
              {
                text: intl.formatMessage({
                  id: 'AlertModal.backButton',
                  defaultMessage: 'Back',
                  description: 'Back button text',
                }),
                level: 'tertiary' as const,
                onClick: () => setStep(1),
              },
              {
                text: intl.formatMessage({
                  id: 'AlertModal.nextStepRecipientsButton',
                  defaultMessage: 'Next Step: Recipients',
                  description: 'Next step recipients button text',
                }),
                level: 'primary' as const,
                onClick: handleUpdateAlert,
              },
            ];
        break;
      case 3:
        title = editMode
          ? 'Edit Alert → Recipients (Step 3/3)'
          : 'Add Alert → Recipients (Step 3/3)';
        actions = editMode
          ? [
              {
                text: intl.formatMessage({
                  id: 'AlertModal.cancelThirdStep',
                  defaultMessage: 'Cancel',
                  description:
                    'Cancel button text for third step in Alert Modal',
                }),
                onClick: handleHide,
                level: 'tertiary' as const,
              },
              {
                text: intl.formatMessage({
                  id: 'AlertModal.saveThirdStep',
                  defaultMessage: 'Save Alert',
                  description:
                    'Save alert button text for third step in Alert Modal',
                }),
                level: 'primary' as const,
                onClick: handleUpdateAlert,
              },
            ]
          : [
              {
                text: intl.formatMessage({
                  id: 'AlertModal.backButton',
                  defaultMessage: 'Back to Filters',
                  description: 'Back button text for third step in Alert Modal',
                }),
                level: 'tertiary' as const,
                onClick: () => setStep(2),
              },
              {
                text: intl.formatMessage({
                  id: 'AlertModal.addAlert',
                  defaultMessage: 'Add Alert',
                  description: 'Label for the add alert button',
                }),
                level: 'primary' as const,
                onClick: handleCreateAlert,
              },
            ];
        break;
    }
    return { title, actions };
  }, [
    step,
    editMode,
    hideModal,
    name,
    message,
    occurrenceInterval,
    isDuplicated,
    step,
    editMode,
    filters,
    config,
    alertId,
    onItemUpdate,
  ]);

  const content = () => {
    switch (step) {
      case 1:
        return (
          <>
            <Layout preset="form-fields" direction="column">
              <FormField<string, false>
                input={TextInput}
                name="name"
                label={intl.formatMessage({
                  id: 'AlertModal.nameLabel',
                  defaultMessage: 'NAME',
                  description: 'Name field label',
                })}
                value={name}
                onChange={(value) =>
                  dispatch(
                    slices.alerts.actions.setConfig({
                      ...config,
                      name: value ?? '',
                    }),
                  )
                }
              />
              <FormField<string, false>
                input={TextArea}
                inputProps={{
                  rows: 6,
                }}
                name="message"
                label={intl.formatMessage({
                  id: 'AlertModal.messageLabel',
                  defaultMessage: 'MESSAGE',
                  description: 'Message field label',
                })}
                value={message}
                onChange={(value) =>
                  dispatch(
                    slices.alerts.actions.setConfig({
                      ...config,
                      message: value ?? '',
                    }),
                  )
                }
              />
              <Section
                title={intl.formatMessage({
                  id: 'AlertModal.schedulingLabel',
                  defaultMessage: 'Scheduling',
                  description: 'Scheduling section label',
                })}
              >
                <Layout align="center" justify="flex-start" spacing={2}>
                  <FormField<number, false>
                    input={NumberInput}
                    inputProps={{
                      min: 1,
                      step: 1,
                      width: '100%',
                    }}
                    name="frequency"
                    value={occurrenceInterval}
                    onChange={(value) =>
                      dispatch(
                        slices.alerts.actions.setConfig({
                          ...config,
                          occurrenceInterval: value ?? null,
                        }),
                      )
                    }
                    label="Frequency"
                  />
                  <FormField<Nullable<string>, false>
                    label="Occurrence Period"
                    input={Select}
                    name="occurrencePeriod"
                    inputProps={{
                      isMulti: false,
                      isClearable: false,
                      options: OCCURRENCE_PERIOD_OPTIONS,
                      width: '100%',
                    }}
                    value={occurrencePeriod?.value ?? null}
                    onChange={(value) => {
                      const selectedOption = OCCURRENCE_PERIOD_OPTIONS.find(
                        (option) => option.value === value,
                      );
                      dispatch(
                        slices.alerts.actions.setConfig({
                          ...config,
                          occurrencePeriod: selectedOption ?? null,
                        }),
                      );
                    }}
                  />
                </Layout>
                <Layout direction="column" spacing={2}>
                  <Layout align="center" justify="flex-start" spacing={2}>
                    <FormField
                      label={intl.formatMessage({
                        id: 'AlertModal.startDate.label',
                        defaultMessage: 'Start Date',
                        description: 'Start date field label',
                      })}
                      inputProps={{
                        isClearable: true,
                        max: occurrenceEndDate
                          ? parseDate(occurrenceEndDate)
                          : null,
                        width: '100%',
                      }}
                      input={enableLocalization ? DateInput : EcDatepicker}
                      name="startDate"
                      value={startDate ? parseDate(startDate) : null}
                      onChange={(date) =>
                        dispatch(
                          slices.alerts.actions.setConfig({
                            ...config,
                            startDate: date ? date : new Date(),
                          }),
                        )
                      }
                    />
                    <FormField
                      label="Ends"
                      input={Select}
                      name="endPeriod"
                      inputProps={{
                        isMulti: false,
                        isClearable: false,
                        options: OCCURRENCE_END_TIMESPAN_OPTIONS,
                        width: '100%',
                      }}
                      value={occurrenceEndTimespan?.value ?? null}
                      onChange={(value) => {
                        const selectedOption =
                          OCCURRENCE_END_TIMESPAN_OPTIONS.find(
                            (option) => option.value === value,
                          );

                        dispatch(
                          slices.alerts.actions.setConfig({
                            ...config,
                            occurrenceEndTimespan: selectedOption || null,
                            ...(selectedOption?.value === 'never'
                              ? {
                                  occurrenceEndDate: null,
                                  occurrenceEndLimit: null,
                                }
                              : selectedOption?.value === 'on_a_specific_date'
                                ? {
                                    occurrenceEndDate: addDays(
                                      parseDate(startDate),
                                      1,
                                    ),
                                    occurrenceEndLimit: null,
                                  }
                                : selectedOption?.value ===
                                    'number_of_occurrences'
                                  ? { occurrenceEndDate: null }
                                  : {}),
                          }),
                        );
                      }}
                    />
                  </Layout>
                  {occurrenceEndTimespan &&
                    'value' in occurrenceEndTimespan &&
                    occurrenceEndTimespan.value !== 'never' && (
                      <Layout align="center" justify="flex-start" spacing={2}>
                        {occurrenceEndTimespan.value ===
                          'on_a_specific_date' && (
                          <FormField
                            label="End Date"
                            input={DateInput}
                            name="occurrenceEndDate"
                            inputProps={{
                              width: '100%',
                              min: startDate
                                ? addDays(parseDate(startDate), 1)
                                : null,
                            }}
                            value={
                              occurrenceEndDate
                                ? parseDate(occurrenceEndDate)
                                : addDays(parseDate(startDate), 1)
                            }
                            onChange={(date) =>
                              dispatch(
                                slices.alerts.actions.setConfig({
                                  ...config,
                                  occurrenceEndDate: date ? date : null,
                                }),
                              )
                            }
                          />
                        )}
                        {occurrenceEndTimespan &&
                          'value' in occurrenceEndTimespan &&
                          occurrenceEndTimespan.value ===
                            'number_of_occurrences' && (
                            <FormField<number, false>
                              label="Number of Occurrences"
                              inputProps={{
                                min: 1,
                                step: 1,
                                width: '100%',
                              }}
                              input={NumberInput}
                              name="occurrenceEndLimit"
                              value={occurrenceEndLimit}
                              onChange={(value) =>
                                dispatch(
                                  slices.alerts.actions.setConfig({
                                    ...config,
                                    occurrenceEndLimit: value,
                                  }),
                                )
                              }
                            />
                          )}
                      </Layout>
                    )}
                </Layout>
              </Section>
              <Section
                title={intl.formatMessage({
                  id: 'AlertModal.foldersLabel',
                  defaultMessage: 'Folders',
                  description: 'Folders section label',
                })}
                description={intl.formatMessage({
                  id: 'AlertModal.foldersDescription',
                  defaultMessage:
                    'You can specify one or more folders to limit the documents being tracked for changes. If no folders are specified, all documents will be tracked.',
                  description: 'Folders section description',
                })}
              >
                <Layout direction="column" spacing={2} align="flex-start">
                  {folders.length ? (
                    <Layout wrap>
                      {folders.map((folder, index) => (
                        <EcMultipleSelectValue
                          key={`folder-${index}`}
                          label={folder?.name}
                          closeHandler={() => {
                            const newFolders = folders.filter(
                              (f) => f?.id !== folder?.id,
                            );
                            dispatch(
                              slices.alerts.actions.setConfig({
                                ...config,
                                folders: newFolders,
                              }),
                            );
                          }}
                        />
                      ))}
                    </Layout>
                  ) : (
                    <Text preset="description">
                      {intl.formatMessage({
                        id: 'AlertModal.noFoldersSelected',
                        defaultMessage: "You haven't selected any folders.",
                        description: 'No folders selected message',
                      })}
                    </Text>
                  )}
                  <Button
                    variant="action"
                    icon="plus"
                    iconPosition="left"
                    text={intl.formatMessage({
                      id: 'AlertModal.addFolderButton',
                      defaultMessage: 'Add a Folder',
                      description: 'Add folder button text',
                    })}
                    onClick={() => setActiveModal(MODAL_FOLDER_TREE)}
                  />
                </Layout>
              </Section>
            </Layout>
          </>
        );
      case 2:
        return (
          <Section
            title={intl.formatMessage({
              id: 'AlertModal.filtersLabel',
              defaultMessage: 'Filters',
              description: 'Filters section label',
            })}
          >
            <EcFiltersSection
              filters={filters}
              filterOptions={filterOptions}
              provisionOptions={provisionOptions}
              onChange={(selectedFilters: typeof filters) => {
                dispatch(
                  slices.alerts.actions.setConfig({
                    ...config,
                    filters: selectedFilters,
                  }),
                );
              }}
              handleFilterOptionsChange={handleOptionsChangeHandler}
              rootSection
            />
          </Section>
        );
      case 3:
        return (
          <Layout preset="sections" direction="column">
            <Section
              title={intl.formatMessage({
                id: 'AlertModal.recipientsLabel',
                defaultMessage: 'Recipients',
                description: 'Recipients section label',
              })}
            >
              <Layout direction="column" spacing={2}>
                <FormField
                  input={Select}
                  name="users"
                  label="Users"
                  value={users}
                  inputProps={{
                    isMulti: true,
                    isClearable: true,
                    options: usersOptions(allUsers),
                    placeholder: intl.formatMessage({
                      id: 'AlertModal.selectFieldsPlaceholder',
                      defaultMessage: 'Search for users...',
                      description:
                        'Placeholder for selecting fields in alert modal',
                    }),
                  }}
                  onChange={(updatedUsers) => {
                    dispatch(
                      slices.alerts.actions.setConfig({
                        ...config,
                        users: updatedUsers,
                      }),
                    );
                  }}
                />
              </Layout>
              <FormField
                input={Select}
                name="userFields"
                label="User Fields"
                value={userFields}
                inputProps={{
                  isMulti: true,
                  isClearable: true,
                  options: userFieldsSuggestions,
                  placeholder: intl.formatMessage({
                    id: 'AlertModal.selectFieldsPlaceholder',
                    defaultMessage: 'Search for fields...',
                    description:
                      'Placeholder for selecting fields in alert modal',
                  }),
                }}
                onChange={(updatedUserFields) => {
                  dispatch(
                    slices.alerts.actions.setConfig({
                      ...config,
                      userFields: updatedUserFields,
                    }),
                  );
                }}
              />
              <Layout direction="column" spacing={2} align="flex-start">
                <Text variant="body-bold">
                  {intl.formatMessage({
                    id: 'AlertModal.departmentsLabel',
                    defaultMessage: 'Departments',
                    description: 'Departments section label',
                  })}
                </Text>
                {departments.length ? (
                  <Layout wrap>
                    {departments.map((department, index) => (
                      <EcMultipleSelectValue
                        key={`department-${index}`}
                        label={department.department_name}
                        closeHandler={() => {
                          const newDepartments = [...departments].filter(
                            (d) => d.department_id !== department.department_id,
                          );
                          dispatch(
                            slices.alerts.actions.setConfig({
                              ...config,
                              departments: newDepartments,
                            }),
                          );
                        }}
                      />
                    ))}
                  </Layout>
                ) : (
                  <Text preset="description">
                    {intl.formatMessage({
                      id: 'AlertModal.noFoldersSelected',
                      defaultMessage: "You haven't selected any folders.",
                      description: 'No folders selected message',
                    })}
                  </Text>
                )}
                <Button
                  text={intl.formatMessage({
                    id: 'AlertModal.addDepartmentButton',
                    defaultMessage: 'Add Department',
                    description: 'Add department button text',
                  })}
                  icon="plus"
                  iconPosition="left"
                  variant="action"
                  onClick={() => setActiveModal(MODAL_DEPARTMENT_TREE)}
                />
              </Layout>
            </Section>
            <Section
              title={intl.formatMessage({
                id: 'AlertModal.excludedUsersLabel',
                defaultMessage: 'Excluded Users',
                description: 'Excluded users section label',
              })}
              description={intl.formatMessage({
                id: 'AlertModal.excludedUsersDescription',
                defaultMessage:
                  'You can exclude users from receiving this alert (i.e. if they are part of a department and would automatically receive it otherwise).',
                description: 'Excluded users section description',
              })}
            >
              <FormField
                input={Select}
                name="excludedUsers"
                label={intl.formatMessage({
                  id: 'AlertModal.excludedUsersLabel',
                  defaultMessage: 'Users',
                  description: 'Excluded users section label',
                })}
                value={excludedUsers}
                inputProps={{
                  isMulti: true,
                  isClearable: true,
                  options: usersOptions(allUsers),
                  placeholder: intl.formatMessage({
                    id: 'AlertModal.selectFieldsPlaceholder',
                    defaultMessage: 'Search for users...',
                    description:
                      'Placeholder for selecting fields in alert modal',
                  }),
                }}
                onChange={(selectedUserIds) => {
                  dispatch(
                    slices.alerts.actions.setConfig({
                      ...config,
                      excludedUsers: selectedUserIds,
                    }),
                  );
                }}
              />
            </Section>
            <Section
              title={intl.formatMessage({
                id: 'AlertModal.permissionsLabel',
                defaultMessage: 'Permissions',
                description: 'Label for the permissions section',
              })}
              description={intl.formatMessage({
                id: 'AlertModal.permissionsInfo',
                defaultMessage:
                  ' By default, only you and admins will be able to edit the alert.',
                description: 'Information about alert permissions',
              })}
            >
              <Checkbox
                id="allow-recipient-edit-alert"
                name="allow-recipient-edit-alert"
                option={{
                  label: intl.formatMessage({
                    id: 'AlertModal.allowRecipientEdit',
                    defaultMessage: 'Allow any recipient to edit this alert',
                    description: 'Label for the allow recipient edit checkbox',
                  }),
                  value: editAccess,
                }}
                value={editAccess}
                onChange={() => {
                  dispatch(
                    slices.alerts.actions.setConfig({
                      ...config,
                      editAccess: !editAccess,
                    }),
                  );
                }}
              />
            </Section>
          </Layout>
        );
    }
  };

  return (
    <>
      <Panel
        title={title}
        loadingContent={{
          isLoading: isLoadingFolderTree || isLoadingAlertModalData,
        }}
        placeholderContent={
          alertModalDataError || folderTreeError
            ? {
                message: intl.formatMessage({
                  id: 'AlertModal.somethingWentWrong',
                  defaultMessage:
                    'Something went wrong with fetching alert modal options.',
                  description:
                    'Error message when fetching alert modal options',
                }),
              }
            : undefined
        }
        children={content()}
        mode="floating"
        width={width ?? '560px'}
        enableBackdrop
        hidden={{ onHide: handleHide }}
        footer={{
          actions: actions?.map((action) => ({
            ...action,
            mode: 'chip',
          })),
        }}
      />
      {activeModal === MODAL_DEPARTMENT_TREE && (
        <EcModal
          modalType={MODAL_DEPARTMENT_TREE}
          width="540px"
          client={clientId}
          title={intl.formatMessage({
            id: 'AlertModal.selectADepartment',
            defaultMessage: 'Select a Department',
            description: 'Title for the select department modal',
          })}
          departmentsSelected={departments}
          handleDepartmentChange={(updatedDepartments: any) => {
            dispatch(
              slices.alerts.actions.setConfig({
                ...config,
                departments: updatedDepartments,
              }),
            );
          }}
          confirmButtonText={intl.formatMessage({
            id: 'AlertModal.selectDepartment',
            defaultMessage: 'Select Department',
            description: 'Label for the select department button',
          })}
          hideModal={() => setActiveModal(null)}
        />
      )}
      {activeModal === MODAL_FOLDER_TREE && (
        <EcModal
          modalType={MODAL_FOLDER_TREE}
          width="540px"
          title={intl.formatMessage({
            id: 'AlertModal.chooseFoldersTo',
            defaultMessage: 'Choose Folders to Search',
            description: 'Title for the choose folders modal',
          })}
          folderIdsSelected={folders.map((folder) => folder.id)}
          confirmButtonText={intl.formatMessage({
            id: 'AlertModal.chooseFolders',
            defaultMessage: 'Choose Folders',
            description: 'Label for the choose folders button',
          })}
          hideModal={() => setActiveModal(null)}
          isMultiSelect={true}
          handleChooseMultipleLocationsClick={(
            updatedFolders: FolderTree[],
          ) => {
            const newFolders = [...folders];
            newFolders.push(...updatedFolders);

            dispatch(
              slices.alerts.actions.setConfig({
                ...config,
                folders: newFolders.flat(Infinity),
              }),
            );
          }}
        />
      )}
    </>
  );
};
